import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { valueRender } from '@repo/toolkit/conditional-render'
import { NumberFormatter } from '@repo/toolkit/number'
import { useNavigator } from '@repo/toolkit/use-navigator'
import AddressLink from '@repo/ui/address-link'
import Badge from '@repo/ui/badge'
import Box from '@repo/ui/box'
import Button from '@repo/ui/button'
import { ActivityIcon, BusThreeIcon, CheckMarkIcon, ChevronLeftIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'

import { getActivity } from '../../api/ols/activities/get-activity'
import LabelWithText from '../../components/elements/label-with-text'
import LoadingScreen from '../../components/elements/loading/loading-screen'
import UserNavBar from '../../components/elements/nav-bar'
import PhoneNumberDisplay from '../../components/elements/phone-number-display'
import { useNativeCountryName } from '../../lib/hooks/use-native-country-name'
import useTranslate from '../../lib/hooks/use-translate'
import { leaderFreeRateEnumMapping } from '../../lib/types/enums/activity/leader-free-rate.enum'
import {
  TransportModeEnum,
  transportModeEnumMapping
} from '../../lib/types/enums/activity/transport-mode.enum'
import NotFoundPage from '../common/not-found'
import { getOlName } from './center/utils/functions'

const OlActivityViewPage = () => {
  const nativeCountryName = useNativeCountryName()
  const { id, olId } = useParams<{ id: string; olId: string }>()
  const { t } = useTranslate()
  const { back } = useNavigator()

  const { data, isPending } = useQuery({
    queryKey: ['get-activity', id, olId],
    queryFn: () => {
      if (!id || isNaN(+id) || !olId || isNaN(+olId)) return
      return getActivity({ id: +id, olId: +olId })
    },
    enabled: !!id && !!olId
  })

  if (isPending) return <LoadingScreen />
  if (!data) return <NotFoundPage />

  return (
    <div>
      <UserNavBar title={getOlName(data.ol)} icon={ActivityIcon} />
      <div className="h-4" />
      <Box>
        <Button
          type="button"
          label="Retour"
          icon={ChevronLeftIcon}
          variant="secondary"
          onClick={() => back()}
          className="mb-4"
        />
        <Typography variant="h6" className="mb-4" color={tailwindColors.black.DEFAULT}>
          {data.name}
        </Typography>
        <div className="grid grid-cols-3 items-center gap-4">
          <Box className="bg-white-hover rounded-lg p-4 border border-grey-light">
            <Typography color={tailwindColors.primary.DEFAULT} variant="text">
              Détails
            </Typography>
            <div className="h-3" />
            <div className="grid grid-cols-2 gap-4 items-center ">
              <LabelWithText label="NOM du lieu" data={data.locationName} />
              <LabelWithText
                label="Numéro de téléphone"
                className="gap-1"
                data={
                  data.phoneNumber ? <PhoneNumberDisplay phoneNumber={data.phoneNumber} /> : '-'
                }
              />
            </div>
          </Box>

          <Box className="bg-white-hover rounded-lg p-4 col-span-2 border border-grey-light">
            <Typography color={tailwindColors.primary.DEFAULT} variant="text">
              Adresse
            </Typography>
            <div className="h-3" />
            <div className="grid grid-cols-5 ">
              <LabelWithText
                label="Adresse"
                data={
                  <AddressLink
                    address1={data.address1}
                    zipCode={data.zipCode}
                    city={data.city}
                    country={nativeCountryName(data.country)}
                  />
                }
              />
              <LabelWithText label="Complément d'adresse" data={valueRender(data.address2)} />
              <LabelWithText label="Ville" data={data.city} />
              <LabelWithText label="Code postal" data={data.zipCode} />
              <LabelWithText label="Pays" data={nativeCountryName(data.country)} />
            </div>
          </Box>
        </div>
        <div className="h-4" />
        <Box className="bg-white-hover rounded-lg p-4 border border-grey-light">
          <Badge
            icon={<ActivityIcon color={tailwindColors.secondary.DEFAULT} />}
            text="Coût de l'activité"
            iconPosition="start"
            size="medium"
            color={tailwindColors.white.dark.DEFAULT}
            textColor={tailwindColors.secondary.DEFAULT}
          />
          <div className="h-3" />
          <div className="grid grid-cols-5 ">
            <LabelWithText label="Split age" data={valueRender(data.activitySplitAge)} />
            <LabelWithText
              label={`Coût / jeune - ${valueRender(data.activitySplitAge)} ans`}
              data={
                <Badge
                  variant="primary"
                  text={NumberFormatter.formatCurrency(
                    data.activityChildCost ?? 0,
                    data.ol.currency
                  )}
                />
              }
            />
            <LabelWithText
              label={`Coût / jeune + ${valueRender(data.activitySplitAge)} ans`}
              data={
                <Badge
                  variant="primary"
                  text={NumberFormatter.formatCurrency(
                    data.activityAdultCost ?? 0,
                    data.ol.currency
                  )}
                />
              }
            />
            <LabelWithText
              label="Coût/encadrant"
              data={
                <Badge
                  variant="primary"
                  text={NumberFormatter.formatCurrency(
                    data.activityLeaderCost ?? 0,
                    data.ol.currency
                  )}
                />
              }
            />
            <LabelWithText
              label="Ratio de gratuité adulte"
              data={t(leaderFreeRateEnumMapping[data.activityLeaderFreeRatio])}
            />
            <LabelWithText
              label="Coût forfaitaire / groupe"
              data={
                <Badge
                  variant="primary"
                  text={NumberFormatter.formatCurrency(
                    data.activityFlatGroupCost ?? 0,
                    data.ol.currency
                  )}
                />
              }
            />
          </div>
        </Box>
        <div className="h-4" />
        <Box className="bg-white-hover rounded-lg p-4 border border-grey-light">
          <Badge
            icon={<BusThreeIcon color={tailwindColors.secondary.DEFAULT} />}
            text="Coût du transport"
            iconPosition="start"
            size="medium"
            color={tailwindColors.white.dark.DEFAULT}
            textColor={tailwindColors.secondary.DEFAULT}
          />
          <div className="h-3" />
          <Badge
            variant="primary"
            iconPosition="start"
            icon={<CheckMarkIcon color={tailwindColors.primary.DEFAULT} height="16" width="16" />}
            text={t(transportModeEnumMapping[data.transportMode])}
          />
          <div className="h-3" />
          {data.transportMode === TransportModeEnum.EXTRA_BUS_OR_TRAIN && (
            <div className="grid grid-cols-5 ">
              <LabelWithText label="Split age" data={valueRender(data.transportSplitAge)} />
              <LabelWithText
                label={`Coût / jeune - ${valueRender(data.transportSplitAge)} ans`}
                data={
                  <Badge
                    variant="primary"
                    text={NumberFormatter.formatCurrency(
                      data.transportChildCost ?? 0,
                      data.ol.currency
                    )}
                  />
                }
              />
              <LabelWithText
                label={`Coût / jeune + ${valueRender(data.transportSplitAge)} ans`}
                data={
                  <Badge
                    variant="primary"
                    text={NumberFormatter.formatCurrency(
                      data.transportAdultCost ?? 0,
                      data.ol.currency
                    )}
                  />
                }
              />
              <LabelWithText
                label="Coût/encadrant"
                data={
                  <Badge
                    variant="primary"
                    text={NumberFormatter.formatCurrency(
                      data.transportLeaderCost ?? 0,
                      data.ol.currency
                    )}
                  />
                }
              />
              <div />

              <LabelWithText
                label="Coût forfaitaire / groupe"
                data={
                  <Badge
                    variant="primary"
                    text={NumberFormatter.formatCurrency(
                      data.transportFlatGroupCost ?? 0,
                      data.ol.currency
                    )}
                  />
                }
              />
            </div>
          )}
        </Box>
      </Box>
    </div>
  )
}

export default OlActivityViewPage
